/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type {
  SnapRecentSearchesStore,
  SearchFormState,
  SnapRecentSearchEntry,
} from '../types';
import { MAX_RECENT_SEARCHES } from '../configs/constants';
import { getRouteKey } from '../utils';
import { flattenSnapRecentSearchesForHistory } from '../utils/snapSearchHistory';
import { snapKeys } from './snap/keys';

export { flattenSnapRecentSearchesForHistory };
export { snapKeys } from './snap/keys';
/** @deprecated Prefer `snapKeys.search.recentSearches` */
export { snapRecentSearchesKeys } from './snap/keys';

const defaultStore: SnapRecentSearchesStore = {
  recentSearches: [],
};

/**
 * Returns the persist query for snap recent searches (`{ data }`).
 */
export const useSnapRecentSearchesQuery = () => {
  return useQuery<SnapRecentSearchesStore>({
    queryKey: snapKeys.search.recentSearches.all,
    queryFn: () => defaultStore,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: false,
  });
};

/**
 * Read recent searches from the persisted React Query cache.
 * Works cross-platform (web via localStorage, mobile via AsyncStorage).
 */
export const useSnapRecentSearches = () => {
  const queryClient = useQueryClient();

  const { data: recentSearchesStore = defaultStore, isLoading } =
    useSnapRecentSearchesQuery();

  const addSnapRecentSearch = useCallback(
    async (
      formState: SearchFormState,
      maxCount?: number,
      fromIsAllAirports?: boolean,
      toIsAllAirports?: boolean,
    ) => {
      const from = formState.originStation;
      const to = formState.destinationStation;

      if (!from || !to) {
        return;
      }

      const existing =
        queryClient.getQueryData<SnapRecentSearchesStore>(
          snapKeys.search.recentSearches.all,
        ) ?? defaultStore;

      const routeKey = getRouteKey(from, to, formState.tripType);

      const newEntry: SnapRecentSearchEntry = {
        id: `${routeKey}-${Date.now()}`,
        from,
        to,
        fromIsAllAirports,
        toIsAllAirports,
        tripType: formState.tripType,
        departDate: formState.selectedDates.depart
          ? format(formState.selectedDates.depart, 'yyyy-MM-dd')
          : '',
        returnDate: formState.selectedDates.return
          ? format(formState.selectedDates.return, 'yyyy-MM-dd')
          : undefined,
        hotelCheckIn: formState.hotelDates.checkIn
          ? format(formState.hotelDates.checkIn, 'yyyy-MM-dd')
          : undefined,
        hotelCheckOut: formState.hotelDates.checkOut
          ? format(formState.hotelDates.checkOut, 'yyyy-MM-dd')
          : undefined,
        selectDifferentDates: formState.selectDifferentDates,
        rooms: formState.rooms,
        timestamp: Date.now(),
      };

      // Remove any existing entry with the same route+tripType
      const filtered = existing.recentSearches.filter(
        (entry) =>
          getRouteKey(entry.from, entry.to, entry.tripType) !== routeKey,
      );

      const nextSearches = [newEntry, ...filtered].slice(
        0,
        maxCount ?? MAX_RECENT_SEARCHES,
      );

      const nextStore: SnapRecentSearchesStore = {
        recentSearches: nextSearches,
      };

      // Update query cache to trigger re-render in all components
      // TanStack Query with QUERY_PERSIST_CACHE will automatically persist to storage
      queryClient.setQueryData(snapKeys.search.recentSearches.all, nextStore);
    },
    [queryClient],
  );

  return {
    recentSearchesStore,
    isLoading,
    addSnapRecentSearch,
  };
};
